import React, { useEffect, useState } from 'react';
import { Icon, Modal, Select, TextField, Tooltip } from '@gerdau/hefesto';
import { RcFile } from 'antd/es/upload/interface';
import { useDispatch, useSelector } from 'react-redux';
import CancelUploadFilesModal from '../../../../../../../components/CancelUploadFilesModal';
import { FabricationProjectsService } from '../../../../../../../services/FabricationProjects/index';
import { loadMockups } from '../../../../../../../store/ducks/fabrication-mockups/actions';
import { loadProjects } from '../../../../../../../store/ducks/fabrication-projects/actions';
import {
  EProjectStructure,
  IFabricationProject
} from '../../../../../../../store/ducks/fabrication-projects/types';
import { sendTagging } from '../../../../../../../store/ducks/tagging/actions';
import { addToast } from '../../../../../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../../../../../store/ducks/toast/types';
import { ApplicationState } from '../../../../../../../store/index';
import { getTaggingInformationScreen } from '../../../../../../../utils/Tagging';
import {
  NewProjectContainer,
  SendProjectBody,
  SendProjectContent,
  SendProjectHeader,
  UploadedFilesList
} from './styles';

interface INewProjectModalProps {
  contractNumber: string;
  uploadedFiles: RcFile[];
  closeModal: (
    uploadedProjects: IFabricationProject[],
    unsendedProjectsFileNames: string[]
  ) => void;
  setExistingProject: (fileName: string) => void;
}

interface INewProjectForm {
  structures?: string[];
  revision?: string;
}

interface IUploadProjectState extends INewProjectForm {
  file: RcFile;
}

const NewProjectModal: React.FC<INewProjectModalProps> = ({
  contractNumber,
  uploadedFiles,
  closeModal,
  setExistingProject
}) => {
  const dispatch = useDispatch();

  const { screenName = '', itemDescription = '' } = getTaggingInformationScreen(
    'modal_send_fabrication-projects'
  );

  const isSizeGreaterThanMaximumSize = (file: RcFile): boolean => {
    const MAX_FILE_SIZE = 51200;
    const fileSizeKiloBytes = file.size / 1024;
    return fileSizeKiloBytes > MAX_FILE_SIZE;
  };

  const getNextAvailableProjectIdx = (currentIdx?: number): number => {
    let availableIdx = 0;

    if (currentIdx !== undefined) {
      const index = uploadedFiles.findIndex(
        (uf, idx) => !isSizeGreaterThanMaximumSize(uf) && idx > currentIdx
      );
      if (index !== -1) {
        availableIdx = index;
      } else {
        availableIdx = currentIdx;
      }
    } else {
      const index = uploadedFiles.findIndex(
        (uf) => !isSizeGreaterThanMaximumSize(uf)
      );
      if (index !== -1) {
        availableIdx = index;
      }
    }
    return availableIdx;
  };
  const [currentProjectIdx, setCurrentProjectIdx] = useState(
    getNextAvailableProjectIdx()
  );

  const [uploadedFabricationProjects, setUploadedFabricationProjects] =
    useState<IFabricationProject[]>([]);

  const [projectsUploadedSuccess, setProjectsUploadedSuccess] = useState<
    number[]
  >([]);

  const [projectsUploadedError, setProjectsUploadedError] = useState<
    { idx: number; error?: string }[]
  >([]);

  const mockupState = useSelector(
    (appState: ApplicationState) => appState.fabricationMockups
  );

  const [isLoading, setIsloading] = useState(false);
  const [canShowCancelUploadFilesModal, setCanShowCancelUploadFilesModal] =
    useState(false);

  const handleLoadMockups = () => {
    dispatch(loadMockups(contractNumber));
  };

  useEffect(() => {
    if (
      mockupState.contractNumber !== contractNumber &&
      !mockupState.loading &&
      !mockupState.error
    ) {
      handleLoadMockups();
    }
  });

  useEffect(() => {
    dispatch(
      sendTagging({
        page: window.location.pathname,
        screenName:
          'LG:Comprar:CorteEDobra:DetalhamentoProjeto:ModalEnvioProjeto',
        event: 'page_view'
      })
    );
  }, []);

  const getInitialState = (): IUploadProjectState[] => {
    return uploadedFiles.map((uf) => {
      return {
        file: uf
      };
    });
  };
  const [uploadProjectState, setUploadProjectState] = useState<
    IUploadProjectState[]
  >(getInitialState());

  const removeCurrentProjectIdxFromProjectsUploadedError = () => {
    if (projectsUploadedError.find((pue) => pue.idx === currentProjectIdx)) {
      setProjectsUploadedError(
        projectsUploadedError.filter((p) => p.idx !== currentProjectIdx)
      );
    }
  };

  const sendSelectedProject = () => {
    const { file, structures, revision } =
      uploadProjectState[currentProjectIdx];
    if (!structures || !revision) {
      return;
    }
    setIsloading(true);

    dispatch(
      sendTagging({
        screenName,
        itemName: 'button:concluir_envio',
        itemDescription: `${itemDescription}_concluirEnvio`,
        event: 'click'
      })
    );

    const handleOnProjectExists = () => {
      onCloseModalProjectExists();
      const { file } = uploadProjectState[currentProjectIdx];
      setExistingProject(file.name);
    };

    FabricationProjectsService.uploadProject(
      contractNumber,
      structures,
      revision,
      file
    )
      .then((fabricationProject) => {
        removeCurrentProjectIdxFromProjectsUploadedError();

        setProjectsUploadedSuccess([
          ...projectsUploadedSuccess,
          currentProjectIdx
        ]);
        setUploadedFabricationProjects([
          ...uploadedFabricationProjects,
          fabricationProject
        ]);
        setIsloading(false);
        if (currentProjectIdx + 1 < uploadProjectState.length) {
          setCurrentProjectIdx(getNextAvailableProjectIdx(currentProjectIdx));
        }

        dispatch(loadProjects(contractNumber));
      })
      .catch((err) => {
        setIsloading(false);
        let message: string | undefined;
        if (err?.response?.data?.status === '400') {
          const messages: string[] =
            Object.values(err?.response?.data?.errors) || [];
          if (messages.length > 0) {
            message = messages[0].concat(
              '. Deseja selecionar o projeto existente?'
            );
            dispatch(
              addToast({
                type: EToastTypes.WARNING,
                message,
                labelActionOne: 'Sim',
                labelActionTwo: 'Não',
                handleClickActionOne: handleOnProjectExists
              })
            );
          }
        } else {
          message = 'Falha ao enviar o arquivo. Tente novamente.';
          dispatch(
            addToast({
              type: EToastTypes.DANGER,
              durationInMS: 5000,
              message
            })
          );
        }

        const newProjectsUploadedError = [
          ...projectsUploadedError.filter((p) => p.idx !== currentProjectIdx)
        ];

        newProjectsUploadedError.push({
          idx: currentProjectIdx,
          error: message
        });

        setProjectsUploadedError(newProjectsUploadedError);
      });
  };

  const isProjectFilled = (): boolean => {
    const { revision, structures } = uploadProjectState[currentProjectIdx];
    return !!revision && structures !== undefined && structures.length > 0;
  };

  const getIcon = (idx: number) => {
    if (projectsUploadedSuccess.includes(idx)) {
      return (
        <Tooltip title="Arquivo enviado">
          <div>
            <Icon variant="success" option="Check" />
          </div>
        </Tooltip>
      );
    } else if (isSizeGreaterThanMaximumSize(uploadProjectState[idx].file)) {
      return (
        <Tooltip title="Não é permitido envio de arquivo maior que 50mb">
          <div>
            <Icon variant="danger" option="Alert" />
          </div>
        </Tooltip>
      );
    } else if (projectsUploadedError.find((pue) => pue.idx === idx)) {
      const pue = projectsUploadedError.find((pue) => pue.idx === idx);
      return (
        <Tooltip
          title={pue?.error || 'Falha ao enviar arquivo. Tente novamente.'}
        >
          <div>
            <Icon variant="danger" option="Alert" />
          </div>
        </Tooltip>
      );
    }
  };

  const getUnsendedProjectsFileNames = (projectExists = false): string[] => {
    const unsendedProjectsFileNames: string[] = [];
    uploadedFiles.forEach((uf, idx) => {
      if (
        (isSizeGreaterThanMaximumSize(uf) && !projectExists) ||
        (!projectsUploadedSuccess.includes(idx) && !projectExists)
      ) {
        unsendedProjectsFileNames.push(uf.name);
      }
    });

    return unsendedProjectsFileNames;
  };

  const onCloseClick = () => {
    dispatch(
      sendTagging({
        screenName,
        itemName: 'button:cancelar',
        itemDescription: `${itemDescription}_cancelar`,
        event: 'click'
      })
    );
    setCanShowCancelUploadFilesModal(true);
  };

  const onCloseModal = () => {
    closeModal(uploadedFabricationProjects, getUnsendedProjectsFileNames());
  };

  const onCloseModalProjectExists = () => {
    closeModal(uploadedFabricationProjects, getUnsendedProjectsFileNames(true));
  };

  return (
    <>
      <Modal
        title="Enviar novo projeto"
        onClose={onCloseClick}
        buttons={
          projectsUploadedSuccess.length === uploadedFiles.length
            ? {
                right: {
                  name: 'close',
                  children: 'Fechar',
                  onClick: onCloseModal
                }
              }
            : {
                left: {
                  name: 'cancel',
                  children: 'Cancelar',
                  onClick: onCloseClick,
                  disabled: isLoading
                },
                right: {
                  name: 'send-file',
                  onClick: sendSelectedProject,
                  children: 'Enviar',
                  disabled:
                    isLoading ||
                    mockupState.loading ||
                    projectsUploadedSuccess.includes(currentProjectIdx) ||
                    isSizeGreaterThanMaximumSize(
                      uploadProjectState[currentProjectIdx].file
                    ) ||
                    !isProjectFilled(),
                  loading: isLoading
                }
              }
        }
      >
        <NewProjectContainer>
          <SendProjectHeader>
            <span className="title">
              Para facilitar a gestão de envio de novos projetos, insira
              informações sobre seus arquivos
            </span>
            <div className="subtitle">
              <span>
                Arquivo {currentProjectIdx + 1} de {uploadProjectState.length}:{' '}
              </span>
              <span>{uploadProjectState[currentProjectIdx].file.name}</span>
            </div>
          </SendProjectHeader>

          <SendProjectBody>
            <UploadedFilesList>
              {uploadProjectState.map((ups, idx) => {
                const selected = currentProjectIdx === idx;
                return (
                  <div
                    key={idx}
                    className={`file${selected ? ' selected' : ''}`}
                    onClick={() => !isLoading && setCurrentProjectIdx(idx)}
                  >
                    <span className="filename" title={ups.file.name}>
                      {ups.file.name}
                    </span>
                    <span className="icon">{getIcon(idx)}</span>
                  </div>
                );
              })}
            </UploadedFilesList>

            <NewProjectForm
              newProjectform={uploadProjectState[currentProjectIdx]}
              updateNewProjectform={(updatedState) => {
                const newState = [...uploadProjectState];
                newState[currentProjectIdx] = {
                  ...newState[currentProjectIdx],
                  ...updatedState
                };
                setUploadProjectState(newState);
              }}
              canUpdateFields={
                !isLoading &&
                !mockupState.loading &&
                !projectsUploadedSuccess.includes(currentProjectIdx) &&
                !isSizeGreaterThanMaximumSize(
                  uploadProjectState[currentProjectIdx].file
                )
              }
            />
          </SendProjectBody>
        </NewProjectContainer>
      </Modal>

      {canShowCancelUploadFilesModal && (
        <CancelUploadFilesModal
          onCancelClick={onCloseModal}
          onContinueClick={() => setCanShowCancelUploadFilesModal(false)}
        />
      )}
    </>
  );
};

interface INewProjectFormProps {
  newProjectform: INewProjectForm;
  updateNewProjectform: (updatedForm: INewProjectForm) => void;
  canUpdateFields: boolean;
}

const NewProjectForm: React.FC<INewProjectFormProps> = ({
  newProjectform,
  updateNewProjectform,
  canUpdateFields
}) => {
  return (
    <SendProjectContent>
      <span className="required-fields-message">
        *Campos com preenchimento obrigatório
      </span>
      <Select
        label="*Estrutura do projeto"
        name="project-structures"
        helperText="Escreva ou utilize as sugestões de estrutura. Ex: Projeto de pilar"
        placeholder="- Selecione a estrutura do projeto -"
        isMultipleSelect
        disabled={!canUpdateFields}
        onSelect={(newValue) =>
          updateNewProjectform({
            ...newProjectform,
            structures: newValue as string[]
          })
        }
        value={newProjectform.structures || []}
        options={Object.values(EProjectStructure).map((structure) => {
          return {
            label: structure,
            value: structure
          };
        })}
      />

      <TextField
        label="*Nº da revisão do projeto"
        name="project-revision"
        placeholder="Escreva o nº da revisão"
        value={newProjectform.revision || ''}
        disabled={!canUpdateFields}
        onChange={(e) =>
          updateNewProjectform({
            ...newProjectform,
            revision: e.target.value
          })
        }
      />
    </SendProjectContent>
  );
};

export default NewProjectModal;
